import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAnchorTooltip } from '../hooks/useAnchorTooltip';
import { LessonTooltip } from './LessonTooltip';
import { LessonGuideShell } from './LessonGuideShell';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { eventBus, EVENTS } from '../../../utils/eventBus';

type Step = 'guide' | 'done';

interface Lesson2GuideProps {
  onClose: () => void;
}

const STEP_TITLES: Record<Step, string> = {
  'guide': 'Draft your day',
  'done': 'Your draft is ready',
};

/**
 * Lesson2Guide
 *
 * Onboarding Lesson 2 的引導卡片（右下角浮動）。
 *
 * 設計原則：所有 Lesson Guide 的職責是引導用戶找到並使用
 * 產品中已存在的功能，而不是把該功能在引導卡片中重新實作一遍。
 *
 * 完成條件：用戶套用計畫後，Calendar.tsx handlePlanApplied →
 * lessonsService.markCompleted('lesson-2') + eventBus.publish(LESSON.PLAN_APPLIED)
 * → Guide 切換到 done 步驟。
 */
export function Lesson2Guide({ onClose }: Lesson2GuideProps) {
  const { setActiveLessonGuide } = useOnboarding();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('guide');
  // 用戶互動後隱藏 tooltip
  const [tooltipHidden, setTooltipHidden] = useState(false);

  // 行動版不顯示 tooltip（行動版有底部浮動按鈕，不需要指向 header 按鈕）
  const isDesktop = !useMediaQuery('(max-width: 640px)');

  useEffect(() => {
    setActiveLessonGuide('lesson-2');
    return () => setActiveLessonGuide(null);
  }, [setActiveLessonGuide]);

  // 用戶點擊 AI Plan 鈕時隱藏 tooltip
  useEffect(() => {
    return eventBus.subscribe(EVENTS.UI.AI_PLAN_TRIGGERED, () => {
      setTooltipHidden(true);
    });
  }, []);

  // AI 計畫套用後，切換到 done 步驟
  useEffect(() => {
    return eventBus.subscribe(EVENTS.LESSON.PLAN_APPLIED, () => {
      setStep('done');
    });
  }, []);

  const calcBottomBelow = useCallback(
    (rect: DOMRect): React.CSSProperties => ({
      position: 'fixed',
      top: rect.bottom + 12,
      right: window.innerWidth - rect.right,
    }),
    [],
  );

  const tooltipStyle = useAnchorTooltip({
    anchor: 'ai-plan-button',
    enabled: isDesktop && step === 'guide' && !tooltipHidden,
    calcStyle: calcBottomBelow,
    initialDelay: 600,
  });

  return (
    <>
      <LessonGuideShell
        lessonId="lesson-2"
        ariaLabel="Lesson 2: Draft today"
        subtitle={step === 'done' ? 'Draft today · Done' : 'Draft today'}
        title={STEP_TITLES[step]}
        onClose={onClose}
        avoidFAB
      >
        {step === 'guide' && (
          <>
            <p className="text-sm text-gray-500 leading-relaxed">
              Tap the{' '}
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-sky-600 text-white text-xs rounded font-medium">
                <Sparkles size={10} />
                Draft
              </span>{' '}
              button.
            </p>
            <p className="text-sm text-gray-500 leading-relaxed">
              Orlo will build a starting schedule from your tasks.
            </p>
            <p className="text-xs text-gray-400">
              On mobile, tap the purple button at the bottom-right.
            </p>
          </>
        )}

        {step === 'done' && (
          <>
            <p className="text-sm text-gray-500 leading-relaxed">
              All set. Head back to Today to continue.
            </p>
            <button
              onClick={() => navigate('/today')}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-sky-600 text-white text-sm font-medium py-2 hover:bg-sky-700 transition-colors"
            >
              Back to today
              <ArrowRight size={14} />
            </button>
          </>
        )}
      </LessonGuideShell>

      {tooltipStyle && (
        <LessonTooltip style={tooltipStyle} arrowAlign="right">
          Tap here to draft your day
        </LessonTooltip>
      )}
    </>
  );
}
